// ---------------------------------------------------------------------------
// ProductSingle (PDP)
// ---------------------------------------------------------------------------
//
// Thin shell for the product detail page. All state, side-effects and
// bridges live in the `ProductSingleProvider`; this file only mounts
// the provider and renders the JSX that reads from the context.
// ---------------------------------------------------------------------------

// Global imports
import {
  BlockPostCarousel,
  BlockProductCarousel,
  Heading,
  IframeVideo,
  Trustpilot,
  FraudCheckNotice,
} from '~/components'
import {
  ProductSingleProvider,
  useProductSingleContext,
} from '~/context/productSingle'
import {
  AddToBasket,
  ProductHeader,
  ImageCarousel,
  VariantOptions,
  ProductDetails,
  PurchaseModal,
  ProductPickupModal,
  ProductFaqs,
  RelatedProducts,
  CheckAddressModal,
  StickyButton,
} from '~/components/partials/product'

// Types
import type { ProductDetailsProps } from '~/types'

// ---------------------------------------------------------------------------
// ProductSingle (wrapper)
// ---------------------------------------------------------------------------

const ProductSingle = (product: ProductDetailsProps) => (
  <ProductSingleProvider product={product}>
    <ProductSingleInner />
  </ProductSingleProvider>
)

export default ProductSingle

// ---------------------------------------------------------------------------
// ProductSingleInner
// ---------------------------------------------------------------------------

const ProductSingleInner = () => {
  const {
    // Product
    product,
    fromPrice,
    salePrice,
    activeVariant,
    selectedVariant,
    thumbsRef,
    mainRef,
    setQueryVariant,

    // Charges result (used to gate the purchase modal)
    charges,

    // Basket / modal flow (still drilled into modals below)
    basketProduct,
    basketError,
    handleAddToBasket,
    chooseBasketFlow,
    resetBasketFlow,
    setAddressCheck,
    addressModalOpen,
    purchaseModalOpen,
    setAddressModalOpen,
    setPurchaseModalOpen,
    resetPricing,

    // Layout refs
    purchaseRef,
    isPurchaseVisible,
  } = useProductSingleContext()

  const {
    description,
    name,
    options,
    hyraxProductType,
    faqs,
    blogPosts,
    recentlyViewed,
    relatedProducts,
    allowSaturday = false,
  } = product

  return (
    <article>
      <div className="mb-10">
        <div className="container mb-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <div className="lg:mb-6">
                <ImageCarousel
                  thumbsRef={thumbsRef}
                  mainRef={mainRef}
                  images={activeVariant?.images}
                  promotionName={activeVariant?.activePromotionName}
                  productName={name}
                />
              </div>
              <div className="hidden lg:block">
                <ProductDetails
                  parentData={{
                    description: activeVariant?.description
                      ? activeVariant.description
                      : (description ?? ''),
                  }}
                  variantData={{
                    title: activeVariant?.name,
                    stockCode: activeVariant?.stockCode,
                    specifications: activeVariant?.specifications,
                    safetyIcons: activeVariant?.safetyIcons,
                    bulletPoints: activeVariant?.detailsBulletPoints,
                  }}
                />
              </div>
            </div>
            <div>
              <ProductHeader
                title={name}
                stockCode={activeVariant?.stockCode}
                shortDescription={activeVariant?.stringAttributes.find(
                  e => e.name == 'shortDescription'
                )}
                requiresFraudCheck={activeVariant?.requiresFraudCheck}
                video={
                  activeVariant?.videos &&
                  activeVariant?.videos?.length > 0 &&
                  activeVariant?.videos[0]?.videoUrl
                    ? true
                    : false
                }
              />
              {options?.values && (
                <div
                  className="mb-6 lg:sticky lg:top-10"
                  ref={purchaseRef}
                >
                  {options?.name !== 'Default' && (
                    <VariantOptions
                      options={options}
                      selected={selectedVariant}
                      onChange={setQueryVariant}
                      clearProductData={resetPricing}
                    />
                  )}
                  {activeVariant?.requiresFraudCheck && (
                    <div className="mb-4">
                      <FraudCheckNotice />
                    </div>
                  )}
                  {activeVariant?.stockCode && (
                    <AddToBasket
                      productType={hyraxProductType}
                      stockCode={activeVariant.stockCode}
                      title={activeVariant?.name}
                      option={activeVariant?.option}
                      allowSaturday={allowSaturday}
                    />
                  )}
                  {basketError && (
                    <p className="mt-3 text-sm text-red-600">{basketError}</p>
                  )}
                </div>
              )}
              <div className="block lg:hidden">
                <ProductDetails
                  parentData={{
                    description: activeVariant?.description
                      ? activeVariant.description
                      : description
                        ? description
                        : '',
                  }}
                  variantData={{
                    title: activeVariant?.name,
                    stockCode: activeVariant?.stockCode,
                    specifications: activeVariant?.specifications,
                    safetyIcons: activeVariant?.safetyIcons,
                    bulletPoints: activeVariant?.detailsBulletPoints,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {recentlyViewed && recentlyViewed.length > 0 && (
          <div className="mb-16">
            <BlockProductCarousel
              title="Recently viewed"
              productCodes={recentlyViewed}
            />
          </div>
        )}
        {activeVariant?.videos && activeVariant?.videos[0]?.videoUrl && (
          <div
            className="container mb-16"
            id="product-video"
          >
            <Heading
              tag="h4"
              size="h4"
              className="text-center"
            >
              Watch our experts to see how it works
            </Heading>
            <IframeVideo
              title={activeVariant.name}
              src={activeVariant?.videos[0]?.videoUrl}
            />
          </div>
        )}
        {faqs && <ProductFaqs faqs={faqs} />}
        {activeVariant?.stockCode && activeVariant?.name && (
          <div
            className="container mb-16"
            id="product-reviews"
          >
            <Trustpilot
              templateId="product"
              product={{
                sku: activeVariant?.stockCode,
                name: activeVariant?.name,
              }}
            />
          </div>
        )}
        {relatedProducts && relatedProducts.length > 0 && (
          <RelatedProducts products={relatedProducts} />
        )}
        {blogPosts && (
          <BlockPostCarousel
            title="Related posts"
            items={blogPosts}
          />
        )}
      </div>
      {charges?.product?.groupId && (
        <PurchaseModal
          open={purchaseModalOpen}
          setOpen={setPurchaseModalOpen}
          onClose={() => {
            resetBasketFlow()
            setPurchaseModalOpen(false)
          }}
          addToBasket={extras => {
            handleAddToBasket({
              product: basketProduct,
              extras,
            })
          }}
        />
      )}
      <CheckAddressModal
        open={addressModalOpen}
        setOpen={setAddressModalOpen}
        onClose={() => {
          resetBasketFlow()
          setAddressModalOpen(false)
        }}
        onProgress={() => {
          setAddressCheck(true)
          setAddressModalOpen(false)
          if (basketProduct && charges) {
            chooseBasketFlow(basketProduct, charges)
          }
        }}
      />
      <ProductPickupModal />
      <StickyButton
        blockRef={purchaseRef}
        isRefVisible={isPurchaseVisible}
        productType={hyraxProductType}
        fromPrice={fromPrice}
        salePrice={salePrice}
      />
    </article>
  )
}
